import React from 'react';
import { connect } from 'react-redux';
import { hideModal } from '../../actions/modal.js';
import { reloadPageifSelectedProductIsOOS } from '../../cart/actions/cart.js';
import { SPIN_THE_WHEEL } from '../../games/constants/gameRouteEntries';
import Loader from './Loader';
import { startAllSteps } from '../../actions/shared/braRecommendation.js';
import { gtmEventPush } from '../../modules/ga/GTMdatalayer.js';

import { getAssetConfig } from '../../refresh/selectors/featureFlagsSelector.ts';
import properties from '../../modules/ajio-util/properties';

  const srOnly = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  };

  function SRAnnounceModalName({ show, name }) {
    const lastAnnounced = React.useRef(null);
    const [msg, setMsg] = React.useState('');

    // Announce on open or when the name changes
    React.useEffect(() => {
      if (!show) return;
      if (name && name !== lastAnnounced.current) {
        setMsg(name); // triggers screen reader announcement
        lastAnnounced.current = name;
      }
    }, [show, name]);

    // Reset memory when the modal hides so re-opening re-announces
    React.useEffect(() => {
      if (!show) {
        lastAnnounced.current = null;
        setMsg(''); // clear live region text
      }
    }, [show]);
    return (
      <div
        aria-live="assertive" // or "polite" if you prefer not to interrupt
        aria-atomic="true"
        style={srOnly}
      >
        {msg}
      </div>
    );
  }

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled]):not([tabindex="-1"])',
    '[href]:not([aria-disabled="true"])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusFirst(container) {
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      el => el.offsetParent !== null
    );
    
    if (!focusables.length) return;

    const el = focusables[0];
    el.setAttribute('data-programmatic-focus', '');
    el.focus();
    el.addEventListener('blur', () => el.removeAttribute('data-programmatic-focus'), { once: true });
  }

class Modal extends React.Component {
  constructor(props) {
    super(props);
    this.firstTimeFocusHandled = false;
  }

  handleKeyboardClicks(event) {
    if (event.keyCode === 13) {
      // Check if the Enter key (key code 13) is pressed
      // event.preventDefault(); // Prevent the default form submission
      document.activeElement.click();
    }
  }

  componentDidMount() {
    this.previousActiveElement = document.activeElement;
    const { id = '', show } = this.props;
    // Capture‐phase focus‐trap:
    document.addEventListener('keydown', this.handleTrapFocus, true);
    // Capture‐phase Escape handler:
    document.addEventListener('keydown', this.handleEscape, true);
    document.addEventListener('keydown', this.handleKeyboardClicks, true);
    document.addEventListener('focusin', this.handleFocusIn, true);
    this.lastFocusedInModal = null;
  }

  handleGAOnCancel = () => {
    const { id = '', fireSegment = () => {} } = this.props;

    if (id.includes('return_fee_post-order')) {
      gtmEventPush({
        event: 'return_fee_halfcard_interaction',
        event_category: 'return fee halfcard interaction',
        event_action: 'cross',
        page_name: 'return fee halfcard screen'
      });
    }

    if (fireSegment) fireSegment();
  };

  handleModalEvent = () => {
    const {
      show = false,
      name,
      closeButton = true,
      className = '',
      id = 'modalId',
      disableOutsideClick = false,
      redirect = false,
      fromPage = '',
      disableOnClickOfCloseButton = false,
      isFromWriteReview,
      displayContent,
      stepsReducer,
      isFromQuickView = false
    } = this.props;

    if (isFromQuickView) {
      const quickViewModal = document.getElementById('quickview-modal-dialog');

      if (quickViewModal) {
        quickViewModal.style.overflowY = 'auto';
      }
    }

    if (redirect) {
      window.history.back();
      return;
    }

    if (!disableOnClickOfCloseButton) {
      this.handleGAOnCancel();
      this.props.hideModal(name);
      if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
        this.props.startAllSteps();
      }
    }
    localStorage.setItem('ga4DressToolFireOnce', JSON.stringify("close"));
    if (this.props.name == 'sign-in-up') {
      ga('send', {
        eventCategory: 'Login',
        eventAction: 'Welcome to AJIO',
        eventLabel: 'Click on X'
      });

      if (fromPage) {
        const page = fromPage ? fromPage.toLowerCase() : '';
        switch (page) {
          case SPIN_THE_WHEEL:
            window.location = '/';
            return;
          default: // do nothing
        }
      }
    }

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  componentDidUpdate(prevProps) {
    const { show, id, name, children } = this.props;
    const justOpened  = !prevProps.show && show;
    const justClosed  =  prevProps.show && !show;
    const contentChanged = show && prevProps.children !== children;



  if (justOpened) {
    this.previousActiveElement = document.activeElement;

    requestAnimationFrame(() => {
      const modalEl = document.getElementById(id || 'modalId');
      if (!modalEl) return;

      // initial focus when opening
        focusFirst(modalEl);

        // Install observer only now
        let rafId;
      const cb = (mutations) => {
        // only act on structural changes
        const structural = mutations.some(m => m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length));
        if (!structural) return;

        // debounce to next frame so DOM settles
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          if (!this.props.show) return;

          const active = document.activeElement;
          const inside = modalEl.contains(active);
          const activeHidden = inside && active instanceof HTMLElement && active.offsetParent === null;

          // if focus is still a valid, visible element inside the modal, do nothing
          if (inside && !activeHidden) return;

          // if the element we *had* in the modal still exists and is visible, restore that
          const preferred = this.lastFocusedInModal;
          if (preferred && modalEl.contains(preferred) && preferred.offsetParent !== null) {
            preferred.focus();
            return;
          }

          // otherwise, go to the first focusable
          focusFirst(modalEl);
        });
      };

      this.domObserver = new MutationObserver(cb);
      this.domObserver.observe(modalEl, { childList: true, subtree: true });
    });
  }

  // When internal step truly swaps (your prev/next children change), we still don’t steal focus
    // unless the current focus got removed/hidden. The observer above handles that.

    if (justClosed) {
      if (this.domObserver) {
        this.domObserver.disconnect();
        this.domObserver = null;
      }
      if (this.previousActiveElement) {
        this.previousActiveElement.focus();
      }
    }
  }

  componentWillUnmount() {
    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }
    document.removeEventListener('keydown', this.handleEscape, true);
    document.removeEventListener('keydown', this.handleTrapFocus, true);
    document.removeEventListener('keydown', this.handleKeyboardClicks, true);
    document.removeEventListener('focusin', this.handleFocusIn, true);
  }

  handleFocusIn = e => {
    if (!this.props.show) return;
    const modalEl = document.getElementById(this.props.id || 'modalId');
    if (!modalEl) return;

    if (modalEl.contains(e.target)) {
      this.lastFocusedInModal = e.target;
      return;
    }

    e.stopImmediatePropagation();
    focusFirst(modalEl);
  };

  handleEscape = event => {
    if (event.key === 'Escape') {
      this.handleModalEvent();
    }
  };

  handleTrapFocus = event => {
    const { show, id } = this.props;
    if (!show || event.key !== 'Tab') return;

    const modalEl = document.getElementById(id || 'modalId');
    if (!modalEl) return;

    const focusables = Array.from(modalEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      el => el.offsetParent !== null
    );

    if (!focusables.length) return;

    const activeEl = document.activeElement;
    if (event.key === 'Tab') {
      const active = document.activeElement;
      if (active.hasAttribute('data-programmatic-focus')) {
        active.removeAttribute('data-programmatic-focus');
        event.preventDefault();
        active.focus();
        return;
      }
    }

    // ─── FIRST TAB: “arm” keyboard focus on the same element ─────────
    if (activeEl.hasAttribute('data-programmatic-focus')) {
      // remove the programmatic-focus flag so :focus-visible fires
      activeEl.removeAttribute('data-programmatic-focus');
      // prevent the browser from moving focus this keypress
        event.preventDefault();
      // leave focus exactly where it is
      return;
    }

    // ─── SUBSEQUENT TABS: cycle normally ───────────────────────────────
    event.preventDefault();
    const idx = focusables.indexOf(activeEl);
    const nextIdx = event.shiftKey
      ? idx > 0
        ? idx - 1
        : focusables.length - 1
      : idx < focusables.length - 1
      ? idx + 1
      : 0;
    focusables[nextIdx].focus();
  };

  render() {
    if (!this.props.show) return null;
    const {
      show = false,
      name,
      closeButton = true,
      className = '',
      id = 'modalId',
      disableOutsideClick = false,
      redirect = false,
      reloadPageifSelectedProductIsOOS,
      disableOnClockOfOutsideModal = false,
      isFromWriteReview,
      displayContent,
      increaseModalHeight = false,
      alignToRight = false,
      fullHeight = false,
      customClassName = '',
      assetConfig = {},
      noBackdrop = false,
    } = this.props;
    let imageBaseUrl = properties.getProperty('imageBaseUrl') || 'https://assets-jiocdn.ajio.com';
    const isJioCDNEnabled = assetConfig && assetConfig.isJioCDNEnabled === 'ON';
    if(isJioCDNEnabled) {
      imageBaseUrl = assetConfig?.jioCDNAssetUrl || '';
    }
    if (!show) {
      return null;
    }
    document.onkeydown = evt => {
      evt = evt || window.event;
      if (evt.keyCode == 27) {
        this.handleGAOnCancel();
        this.props.hideModal(this.props.name);
        if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
          this.props.startAllSteps();
        }
        if (this.props.onClose) {
          this.props.onClose();
        }
      }
    };
    let closeBtnNode = null;
    if (closeButton) {
      closeBtnNode = (
        <div
          id={`closeBtn-${name}`}
          className="ic-close-quickview"
          onClick={this.handleModalEvent}
          aria-label="close"
          role="button"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.handleModalEvent();
            }
          }}
          tabIndex={0}
        />
      );
    }
    return (
      <div
        className={`modal fade in ${className} ${customClassName} ${noBackdrop ? 'no-backdrop' : ''}`}
        style={{ display: 'block' }}
        id={id}
        aria-modal="true"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-hidden={!show}
        onClick={() => {
          if (name == 'cart-qty') {
            reloadPageifSelectedProductIsOOS();
          }
          if (!disableOutsideClick && !disableOnClockOfOutsideModal) {
            this.handleGAOnCancel();
            this.props.hideModal(name);
            if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
              this.props.startAllSteps();
            }
            if (this.props.onClose) {
              this.props.onClose();
            }
          }
        }}
      >
        {this.props.createLoader && <Loader name={this.props.loaderIdentifier} />}
        <div
          className={`modal-dialog ${increaseModalHeight ? 'modal-dialog-increased-height' : ''} ${isFromWriteReview ? 'is-write-review-card' : ''
            } ${alignToRight ? 'modal-right-aligned' : ''} ${fullHeight ? 'modal-content-full-h' : ''}`}
          id={id === 'quickview-modal' ? 'quickview-modal-dialog' : ''}
          onClick={e => {
            return e.stopPropagation();
          }}
        >
          <div
            className={`modal-content ${fullHeight ? 'modal-content-full-h' : ''}`}
            // style={{
            //   backgroundImage: `url(${`${imageBaseUrl}/static/img/catnav_mob_bg.png`})`,
            //   backgroundRepeat: 'repeat-x'
            //  }}
            onClick={e => {
              return e.stopPropagation();
            }}
          >
            <SRAnnounceModalName show={show} name={this.props.ariaLabel ?? name} />
            {closeBtnNode}
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let otherProps = {
    fromPage: '',
    disableOnClickOfCloseButton: false,
    disableOnClockOfOutsideModal: false,
    isFromWriteReview: false,
    displayContent: '',
    increaseModalHeight: false
  };
  const { modals = {}, stepsReducer } = state;
  const { name, displayContent, increaseModalHeight } = ownProps;
  const assetConfig = getAssetConfig(state?.flags);
  let show = false;
  if (modals[name]) {
    show = true;
    const { state: modalState = {}, ariaLabel } = modals[name] || {};
    otherProps = {
      ...otherProps,
      displayContent,
      ...(modalState || {})
    };
    if (modalState.isFromWriteReview) {
      otherProps.isFromWriteReview = true;
    }
    if (ariaLabel) {
      otherProps.ariaLabel = ariaLabel;
    }
  }

  if (name === 'sign-in-up') {
    const { state: modalState = {} } = modals[name] || {};
    otherProps = {
      ...otherProps,
      ...(modalState || {}),
      assetConfig
    };
  }
  if (increaseModalHeight) {
    otherProps = {
      ...otherProps,
      increaseModalHeight: true
    };
  }
  return {
    show,
    stepsReducer,
    ...otherProps
  };
};

function mapDispatchToProps(dispatch) {
  return {
    hideModal: name => {
      dispatch(hideModal(name));
    },
    reloadPageifSelectedProductIsOOS: () => dispatch(reloadPageifSelectedProductIsOOS()),
    startAllSteps: () => dispatch(startAllSteps()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
